import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Heart, Menu, X, ShieldCheck, Bookmark, Sparkles, UserCheck, PhoneCall, Wallet, Camera, User, Smartphone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PairUpLogo } from './common/PairUpLogo';
import { LanguageSwitcher } from './common/LanguageSwitcher';
import { useLanguage } from '../i18n/LanguageContext';

interface NavbarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onOpenAuth: (mode: 'login' | 'signup', accountType?: 'client' | 'companion') => void;
  onOpenWishlist: () => void;
  wishlistCount: number;
  onOpenSafety: () => void;
  onOpenMembership: () => void;
  onOpenAbout?: () => void;
  onOpenHowItWorks?: () => void;
  currentUser?: string | null;
  onLogout?: () => void;
  onOpenProfile: (tab?: 'profile' | 'wallet') => void;
  userAvatarUrl?: string;
  walletBalance?: number;
  isCompanion?: boolean;
  onOpenCompanionDashboard?: () => void;
  onOpenPermissions?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  activeTab,
  setActiveTab,
  onOpenAuth,
  onOpenWishlist,
  wishlistCount,
  onOpenSafety,
  onOpenMembership,
  onOpenAbout,
  onOpenHowItWorks,
  currentUser,
  onLogout,
  onOpenProfile,
  userAvatarUrl = 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=600&q=80',
  walletBalance = 0,
  isCompanion = false,
  onOpenCompanionDashboard,
  onOpenPermissions,
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const rawNavLinks = [
    { id: 'home', label: t('nav.home', 'Home') },
    { id: 'about', label: 'About' },
    { id: 'how-it-works', label: t('nav.howItWorks', 'How It Works') },
    { id: 'browse', label: t('nav.findCompanions', 'Browse') },
    ...(isCompanion
      ? [{ id: 'membership', label: t('nav.membership', 'Membership') }]
      : [{ id: 'bookings', label: t('nav.myBookings', 'My Bookings') }]),
    { id: 'safety', label: t('nav.safety', 'Safety') },
    { id: 'blog', label: 'Reviews' },
  ];

  const navLinks = rawNavLinks;

  const handleNavClick = (id: string) => {
    setActiveTab(id);
    setMobileMenuOpen(false);

    if (id === 'home') {
      navigate('/home');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (id === 'about') {
      const el = document.getElementById('about-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      } else if (onOpenAbout) {
        onOpenAbout();
      }
    } else if (id === 'how-it-works') {
      const el = document.getElementById('how-it-works-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
      }
    } else if (id === 'browse') {
      navigate('/companions');
    } else if (id === 'bookings') {
      navigate('/my-bookings');
    } else if (id === 'safety') {
      onOpenSafety();
    } else if (id === 'membership') {
      navigate('/membership');
    } else if (id === 'blog') {
      const el = document.getElementById('reviews-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
      }
    }
  };

  return (
    <header
      id="main-header"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-xs py-3 border-b border-pink-100'
          : 'bg-white/80 backdrop-blur-xs py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Brand Logo */}
        <div
          id="brand-logo-btn"
          onClick={() => handleNavClick('home')}
          className="flex items-center gap-2.5 cursor-pointer group"
        >
          <div className="relative group-hover:scale-105 transition-transform flex items-center justify-center">
            <PairUpLogo className="w-11 h-11" />
            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white" title="Verified Safe Platform" />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center tracking-tight">
              <span className="font-extrabold text-xl text-slate-900 font-sans">PairUp</span>
              <span className="font-extrabold text-xl text-pink-600 font-sans ml-0.5">Today</span>
            </div>
            <span className="text-[10px] font-semibold text-slate-500 -mt-1 tracking-wide">
              Find Your Perfect Companion
            </span>
          </div>
        </div>

        {/* Desktop Navigation Links */}
        <nav id="desktop-nav" className="hidden md:flex items-center space-x-1 lg:space-x-2">
          {navLinks.map((link) => {
            const isActive = activeTab === link.id;
            return (
              <button
                key={link.id}
                id={`nav-link-${link.id}`}
                onClick={() => handleNavClick(link.id)}
                className={`relative px-3.5 py-2 text-sm font-semibold transition-colors duration-200 cursor-pointer ${
                  isActive ? 'text-pink-600' : 'text-slate-700 hover:text-pink-600'
                }`}
              >
                {link.label}
                {isActive && (
                  <span className="absolute bottom-0 left-3.5 right-3.5 h-0.5 bg-pink-600 rounded-full" />
                )}
              </button>
            );
          })}
        </nav>

        {/* Action Buttons */}
        <div id="nav-actions" className="flex items-center gap-2 sm:gap-3">
          {/* Language Switcher (EN / বাংলা / हिन्दी) */}
          <LanguageSwitcher variant="compact" className="hidden sm:inline-flex" />

          {/* Privacy & Permissions Hub - 100% User Consent Controls */}
          {onOpenPermissions && (
            <button
              id="permissions-quick-btn"
              onClick={onOpenPermissions}
              className="p-2 text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-colors cursor-pointer relative"
              title="Privacy, Security & Permissions Hub"
            >
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
            </button>
          )}

          {/* Wishlist button */}
          <button
            id="wishlist-toggle-btn"
            onClick={onOpenWishlist}
            className="relative p-2 text-slate-600 hover:text-pink-600 hover:bg-pink-50 rounded-full transition-colors cursor-pointer"
            title="Saved Favorites"
          >
            <Heart className="w-5 h-5" />
            {wishlistCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow-xs animate-bounce">
                {wishlistCount}
              </span>
            )}
          </button>

          {/* Auth State */}
          {currentUser ? (
            <div className="flex items-center gap-1.5 sm:gap-2">
              {isCompanion ? (
                <>
                  {/* Quick Earnings Wallet Button - For companion hosts */}
                  <button
                    type="button"
                    id="nav-wallet-btn"
                    onClick={() => navigate('/dashboard')}
                    className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100/90 text-emerald-800 border border-emerald-300 rounded-full font-bold text-xs shadow-xs transition-all cursor-pointer hover:scale-102"
                    title="Earnings Wallet"
                  >
                    <div className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
                      <Wallet className="w-3 h-3" />
                    </div>
                    <span className="font-extrabold font-mono">₹{walletBalance.toLocaleString('en-IN')}</span>
                  </button>

                  {/* Companion Dashboard Shortcut Button */}
                  <button
                    type="button"
                    id="nav-companion-dashboard-btn"
                    onClick={() => navigate('/dashboard')}
                    className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-800 border border-purple-200 rounded-full font-bold text-xs shadow-xs transition-all cursor-pointer hover:scale-102"
                    title="Dashboard Hub"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                    <span className="hidden sm:inline">Dashboard</span>
                  </button>
                </>
              ) : (
                <>
                  {/* Client My Bookings Shortcut */}
                  <button
                    type="button"
                    id="nav-client-bookings-btn"
                    onClick={() => navigate('/my-bookings')}
                    className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-800 border border-slate-200 rounded-full font-bold text-xs shadow-2xs transition-all cursor-pointer"
                    title="My Companion Bookings"
                  >
                    <span>My Bookings</span>
                  </button>
                </>
              )}

              {/* User Profile with Avatar & Link to My Account */}
              <button
                type="button"
                id="nav-user-profile-btn"
                onClick={() => navigate('/my-account')}
                className="flex items-center gap-1.5 sm:gap-2 pl-1 pr-2.5 sm:pr-3 py-1 bg-gradient-to-r from-pink-50 to-purple-50 hover:from-pink-100 hover:to-purple-100 border border-pink-200 rounded-full shadow-xs transition-all cursor-pointer group"
                title="My Account & Profile"
              >
                <div className="relative w-7 h-7 rounded-full overflow-hidden border-2 border-pink-500 shrink-0 shadow-xs">
                  <img
                    src={userAvatarUrl}
                    alt={currentUser}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="w-3 h-3 text-white" />
                  </div>
                </div>
                <span className="text-xs font-bold text-slate-800 max-w-[85px] sm:max-w-[105px] truncate" title={currentUser}>
                  {currentUser}
                </span>
                <span className="text-[10px] bg-emerald-100 text-emerald-700 font-extrabold px-1.5 py-0.2 rounded-md hidden sm:inline-block">
                  {isCompanion ? 'Host' : 'Client'}
                </span>
              </button>

              <button
                onClick={onLogout}
                className="text-xs font-semibold text-slate-500 hover:text-red-500 px-1.5 sm:px-2 py-1 transition-colors cursor-pointer"
                title="Log Out"
              >
                Logout
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Become a Companion Button with Earn Up to ₹1 Lakh badge */}
              <button
                type="button"
                id="nav-become-companion-btn"
                onClick={() => navigate('/companion/register')}
                className="hidden md:flex items-center gap-2 text-xs font-bold text-purple-900 bg-gradient-to-r from-purple-100 via-pink-50 to-purple-100 hover:from-purple-200 hover:to-pink-100 px-3.5 py-1.5 rounded-full border border-purple-300 shadow-2xs cursor-pointer transition-all hover:scale-102 group"
              >
                <span className="flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-purple-600 group-hover:rotate-12 transition-transform" />
                  <span>{t('nav.becomeCompanion', 'Become a Companion')}</span>
                </span>
                <span className="text-[10px] sm:text-[11px] font-extrabold text-white bg-gradient-to-r from-purple-600 to-pink-600 px-2 py-0.5 rounded-full shadow-xs">
                  Earn Up to ₹1 Lakh
                </span>
              </button>

              {/* Login Button */}
              <Link
                to="/login"
                id="nav-login-btn"
                className="px-3.5 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-pink-600 hover:text-pink-700 bg-pink-50 hover:bg-pink-100/80 border border-pink-200 rounded-full transition-all duration-200 cursor-pointer"
              >
                {t('nav.login', 'Login')}
              </Link>

              {/* Sign Up Button */}
              <Link
                to="/signup"
                id="nav-signup-btn"
                className="px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 rounded-full shadow-md shadow-pink-500/25 hover:shadow-pink-500/40 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer flex items-center gap-1.5"
              >
                <span>{t('nav.register', 'Sign Up')}</span>
                <span className="px-1.5 py-0.2 bg-emerald-400 text-emerald-950 text-[10px] font-black rounded-full uppercase">
                  FREE
                </span>
              </Link>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            id="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-slate-700 hover:text-pink-600 rounded-lg focus:outline-none"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div id="mobile-menu-drawer" className="md:hidden bg-white border-b border-pink-100 px-4 pt-3 pb-6 space-y-3 shadow-lg">
          <div className="flex items-center justify-between px-2 pb-2 border-b border-slate-100">
            <span className="text-xs font-bold text-slate-600">{t('common.language', 'Language')}</span>
            <LanguageSwitcher variant="pills" />
          </div>
          <div className="flex flex-col space-y-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                id={`mobile-nav-${link.id}`}
                onClick={() => handleNavClick(link.id)}
                className={`text-left px-4 py-2.5 rounded-xl font-medium text-sm transition-colors ${
                  activeTab === link.id
                    ? 'bg-pink-50 text-pink-600 font-semibold'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="pt-2 border-t border-slate-100 flex flex-col gap-2">
            {/* Mobile Profile & Wallet triggers - ONLY when logged in */}
            {currentUser ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    id="mobile-profile-btn"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      onOpenProfile('profile');
                    }}
                    className="flex items-center justify-center gap-2 py-2.5 px-3 text-xs font-bold text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-xl"
                  >
                    <Camera className="w-4 h-4 text-pink-600" />
                    <span>Profile & Photo</span>
                  </button>

                  <button
                    type="button"
                    id="mobile-wallet-btn"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      onOpenProfile('wallet');
                    }}
                    className="flex items-center justify-center gap-2 py-2.5 px-3 text-xs font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl"
                  >
                    <Wallet className="w-4 h-4 text-emerald-600" />
                    <span>Wallet (₹{walletBalance.toLocaleString('en-IN')})</span>
                  </button>
                </div>

                <button
                  type="button"
                  id="mobile-logout-btn"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onLogout?.();
                  }}
                  className="w-full py-2 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-xl transition-colors text-center"
                >
                  Logout ({currentUser})
                </button>
              </>
            ) : (
              <div className="space-y-2">
                {/* Mobile Become a Companion Banner Button */}
                <button
                  type="button"
                  id="mobile-become-companion-btn"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onOpenAuth('signup', 'companion');
                  }}
                  className="w-full p-2.5 rounded-xl bg-gradient-to-r from-purple-900 via-purple-950 to-slate-900 border border-purple-700/80 text-white flex items-center justify-between shadow-md cursor-pointer hover:border-purple-500 transition-all text-left"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-purple-600/30 border border-purple-500/40 flex items-center justify-center text-purple-300 shrink-0">
                      <Sparkles className="w-4 h-4 text-purple-300" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-white flex items-center gap-1">
                        <span>Become a Companion</span>
                      </div>
                      <div className="text-[10px] text-purple-300 font-medium">Verified bookings & safe payouts</div>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[10px] font-black rounded-lg shadow-xs shrink-0">
                    Earn Up to ₹1 Lakh
                  </span>
                </button>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    id="mobile-login-btn"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      onOpenAuth('login');
                    }}
                    className="w-full py-2.5 text-xs font-bold text-pink-600 bg-pink-50 hover:bg-pink-100 border border-pink-200 rounded-xl transition-all text-center"
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    id="mobile-signup-btn"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      onOpenAuth('signup', 'client');
                    }}
                    className="w-full py-2.5 text-xs font-bold text-white bg-gradient-to-r from-pink-500 to-rose-600 rounded-xl shadow-xs text-center flex items-center justify-center gap-1.5"
                  >
                    <span>Sign Up Free</span>
                    <span className="px-1.5 py-0.2 bg-emerald-400 text-emerald-950 text-[9px] font-black rounded-full uppercase">
                      ₹0
                    </span>
                  </button>
                </div>
              </div>
            )}

            <button
              id="mobile-safety-btn"
              onClick={() => {
                setMobileMenuOpen(false);
                onOpenSafety();
              }}
              className="flex items-center justify-center gap-2 w-full py-2.5 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-xl"
            >
              <ShieldCheck className="w-4 h-4" /> 100% Verified & Safe Rules
            </button>

            {onOpenPermissions && (
              <button
                id="mobile-permissions-btn"
                onClick={() => {
                  setMobileMenuOpen(false);
                  onOpenPermissions();
                }}
                className="flex items-center justify-center gap-2 w-full py-2.5 text-xs font-semibold text-indigo-700 bg-indigo-50 rounded-xl cursor-pointer"
              >
                <Smartphone className="w-4 h-4" /> Device Permissions & Privacy
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
